import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import {
  Plus,
  Search,
  KeyRound,
  Pencil,
  Trash2,
  UserCheck,
  UserX,
  Loader2,
  ShieldCheck,
  Shield,
  User as UserIcon,
  RefreshCw,
} from "lucide-react";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import TableSkeleton from "@/components/TableSkeleton";
import RowActionsMenu from "@/components/RowActionsMenu";
import Banner from "@/components/Banner";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const ROLE_META = {
  admin: { label: "Admin", color: "bg-rose-100 text-rose-700 border-rose-200", icon: ShieldCheck },
  hr_executive: { label: "HR Executive", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Shield },
  employee: { label: "Employee", color: "bg-slate-100 text-slate-700 border-slate-200", icon: UserIcon },
};

const ACTION_LIST = [
  { key: "view", label: "View" },
  { key: "add", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
  { key: "approve", label: "Approve" },
  { key: "export", label: "Export" },
];

// Compute the union of permissions across the assigned roles
function computeEffective(roleIds, rolesById, modules) {
  const modList = modules?.length ? modules : [];
  const eff = {};
  modList.forEach((m) => {
    eff[m.key] = {};
    ACTION_LIST.forEach((a) => { eff[m.key][a.key] = false; });
  });
  (roleIds || []).forEach((rid) => {
    const role = rolesById[rid];
    if (!role) return;
    if (rid === "role_admin") {
      modList.forEach((m) => ACTION_LIST.forEach((a) => { eff[m.key][a.key] = true; }));
      return;
    }
    modList.forEach((m) => {
      ACTION_LIST.forEach((a) => {
        if (role.permissions?.[m.key]?.[a.key]) eff[m.key][a.key] = true;
      });
    });
  });
  return eff;
}

function RoleChips({ availableRoles, selectedIds, onChange, autoNotice }) {
  const selected = (selectedIds || []).map((id) => availableRoles.find((r) => r.id === id)).filter(Boolean);
  const unselected = availableRoles.filter((r) => !(selectedIds || []).includes(r.id));
  return (
    <div className="space-y-2" data-testid="role-chips">
      <div className="flex flex-wrap items-center gap-1.5 min-h-[34px] border border-slate-200 rounded p-1.5 bg-slate-50">
        {selected.length === 0 && (
          <span className="text-xs text-slate-400 px-1">No roles assigned</span>
        )}
        {selected.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-1 text-xs bg-sky-100 text-sky-700 border border-sky-200 rounded-full pl-2 pr-1 py-0.5">
            {r.name}
            <button
              type="button"
              onClick={() => onChange((selectedIds || []).filter((x) => x !== r.id))}
              className="p-0.5 hover:bg-sky-200 rounded-full"
              data-testid={`remove-role-${r.id}`}
              title={`Remove ${r.name}`}
            >
              <span className="text-xs leading-none">×</span>
            </button>
          </span>
        ))}
      </div>
      {unselected.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-slate-500">Add role:</span>
          {unselected.map((r) => (
            <button
              type="button"
              key={r.id}
              onClick={() => onChange([...(selectedIds || []), r.id])}
              className="inline-flex items-center gap-1 text-xs border border-slate-200 hover:bg-slate-50 rounded-full px-2 py-0.5 text-slate-700"
              data-testid={`add-role-${r.id}`}
            >
              + {r.name}
            </button>
          ))}
        </div>
      )}
      {autoNotice && (
        <div className="text-[11px] text-slate-500 italic">{autoNotice}</div>
      )}
    </div>
  );
}

function EffectivePermissionsPreview({ roleIds, rolesById, modules = [] }) {
  const eff = useMemo(
    () => computeEffective(roleIds, rolesById, modules),
    [roleIds, rolesById, modules],
  );
  return (
    <div className="border border-slate-200 rounded overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
        Effective Permissions (union of all assigned roles)
      </div>
      <div className="overflow-x-auto max-h-72">
        <table className="w-full text-xs" data-testid="effective-perms-table">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr>
              <th className="text-left px-3 py-1.5 font-semibold">Module</th>
              {ACTION_LIST.map((a) => (
                <th key={a.key} className="px-2 py-1.5 font-semibold text-center">{a.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m.key} className="border-t border-slate-100">
                <td className="px-3 py-1.5 text-slate-700 font-medium">
                  {m.label}
                  {m.group && <span className="block text-[10px] text-slate-400 font-normal">{m.group}</span>}
                </td>
                {ACTION_LIST.map((a) => {
                  const on = !!eff[m.key]?.[a.key];
                  return (
                    <td key={a.key} className="px-2 py-1.5 text-center" data-testid={`eff-${m.key}-${a.key}`}>
                      {on ? (
                        <span className="inline-block w-4 h-4 bg-emerald-100 text-emerald-700 rounded text-[10px] leading-4 font-bold">✓</span>
                      ) : (
                        <span className="inline-block w-4 h-4 text-slate-300 text-[10px] leading-4">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY_CREATE = {
  email: "",
  name: "",
  password: "",
  role: "hr_executive",
  must_change_password: true,
  role_ids: [],
};


export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");

  // Unified create/edit modal — `formMode` is "create" | "edit", `editing` holds the user when editing
  const [formMode, setFormMode] = useState(null); // null = closed
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CREATE);
  const [formErr, setFormErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [resetting, setResetting] = useState(null); // user object
  const [resetForm, setResetForm] = useState({ new_password: "", must_change_password: true });
  const [resetErr, setResetErr] = useState("");

  const [deleting, setDeleting] = useState(null);

  // RBAC roles catalog (loaded once)
  const [availableRoles, setAvailableRoles] = useState([]);
  const [permModules, setPermModules] = useState([]);
  const rolesById = useMemo(
    () => Object.fromEntries(availableRoles.map((r) => [r.id, r])),
    [availableRoles],
  );

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (activeFilter) params.active = activeFilter === "active";
      // Phase 10 fix — always refetch roles so newly created roles appear in the dropdown
      const [u, r, c] = await Promise.all([
        api.get("/users", { params }),
        api.get("/roles"),
        api.get("/roles/catalog").catch(() => ({ data: { modules: [] } })),
      ]);
      setUsers(u.data);
      setAvailableRoles(r.data || []);
      setPermModules(c.data?.modules || []);
    } catch (e) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, activeFilter]);

  // Live search (debounced) — same feel as Employees directory
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const counts = useMemo(() => {
    const c = { total: users.length, admin: 0, hr_executive: 0, employee: 0, inactive: 0 };
    for (const u of users) {
      c[u.role] = (c[u.role] || 0) + 1;
      if (u.active === false) c.inactive += 1;
    }
    return c;
  }, [users]);

  // Phase 14 — sortable users table
  const { items: sortedUsers, sortBy, sortDir, requestSort } = useSortableData(users, { initialKey: "name" });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_CREATE);
    setFormErr("");
    setFormMode("create");
  };

  const openEdit = (u) => {
    let roleIds = [...(u.role_ids || [])];
    // Primary Employee login role must stay in the union (with Payroll Admin, etc.)
    if (u.role === "employee" && !roleIds.includes("role_employee")) {
      roleIds = ["role_employee", ...roleIds];
    }
    if (u.role === "hr_executive" && !roleIds.includes("role_hr_exec")) {
      roleIds = ["role_hr_exec", ...roleIds];
    }
    setEditing(u);
    setForm({
      ...EMPTY_CREATE,
      email: u.email,
      name: u.name || "",
      role: u.role,
      role_ids: roleIds,
    });
    setFormErr("");
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode(null);
    setEditing(null);
    setFormErr("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormErr("");
    try {
      let roleIds = [...(form.role_ids || [])];
      if (form.role === "employee" && !roleIds.includes("role_employee")) {
        roleIds = ["role_employee", ...roleIds];
      }
      if (form.role === "hr_executive" && !roleIds.includes("role_hr_exec")) {
        roleIds = ["role_hr_exec", ...roleIds];
      }
      if (formMode === "create") {
        const payload = {
          email: form.email,
          name: form.name,
          password: form.password,
          role: form.role,
          must_change_password: form.must_change_password,
          role_ids: roleIds,
        };
        // Omit permissions so backend applies HR-exec defaults when needed
        await api.post("/users", payload);
      } else {
        const payload = {
          name: form.name,
          role: form.role,
          role_ids: roleIds,
        };
        await api.put(`/users/${editing.id}`, payload);
      }
      closeForm();
      load();
    } catch (ex) {
      setFormErr(ex?.response?.data?.detail || (formMode === "create" ? "Failed to create user" : "Failed to update"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    if (!window.confirm(`${u.active ? "Deactivate" : "Activate"} ${u.email}?`)) return;
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Failed");
    }
  };

  const openReset = (u) => {
    setResetting(u);
    setResetForm({ new_password: "", must_change_password: true });
    setResetErr("");
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetErr("");
    try {
      await api.post(`/users/${resetting.id}/reset-password`, resetForm);
      setResetting(null);
    } catch (ex) {
      setResetErr(ex?.response?.data?.detail || "Failed to reset");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/users/${deleting.id}`);
      setDeleting(null);
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Failed");
    }
  };

  return (
    <AdminLayout title="User Management" breadcrumbs={["Home", "Settings", "User Management"]}>
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard label="Total Users" value={counts.total} color="bg-[#226DB4]" />
        <StatCard label="Admins" value={counts.admin} color="bg-rose-500" />
        <StatCard label="HR Executives" value={counts.hr_executive} color="bg-amber-500" />
        <StatCard label="Inactive" value={counts.inactive} color="bg-slate-500" />
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm">
        {/* Toolbar — JDE-style inquiry bar */}
        <div className="px-4 py-2.5 border-b border-slate-300 bg-[#F0F4F8] flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-slate-800 tracking-wide uppercase">User Security — Work With Users</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Primary role + all assigned RBAC roles shown in the grid</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Find name or email…"
                className="border border-slate-300 rounded-sm pl-7 pr-2 py-1.5 text-sm w-56 bg-white"
                data-testid="users-search-input"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm bg-white"
              data-testid="users-role-filter"
            >
              <option value="">All Primary Roles</option>
              <option value="admin">Admin</option>
              <option value="hr_executive">HR Executive</option>
              <option value="employee">Employee</option>
            </select>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm bg-white"
              data-testid="users-active-filter"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              onClick={load}
              title="Refresh"
              data-testid="users-refresh-btn"
              className="p-1.5 border border-slate-300 rounded-sm hover:bg-white text-slate-600 bg-white"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 bg-[#089949] hover:bg-[#067A3A] text-white px-3 py-1.5 text-sm rounded-sm font-medium"
              data-testid="add-user-btn"
            >
              <Plus className="h-4 w-4" /> Add User
            </button>
          </div>
        </div>

        {/* JDE-style inquiry grid */}
        <div className="overflow-x-auto border-t border-slate-300">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="bg-[#E8EEF4] border-b-2 border-slate-400">
                <SortableTH columnKey="name" active={sortBy} dir={sortDir} onClick={requestSort} className="!bg-transparent !text-[11px] !font-bold !text-slate-700 !uppercase !tracking-wide !px-3 !py-2 border-r border-slate-300">
                  User Name
                </SortableTH>
                <SortableTH columnKey="email" active={sortBy} dir={sortDir} onClick={requestSort} className="!bg-transparent !text-[11px] !font-bold !text-slate-700 !uppercase !tracking-wide !px-3 !py-2 border-r border-slate-300">
                  Login / Email
                </SortableTH>
                <SortableTH columnKey="role" active={sortBy} dir={sortDir} onClick={requestSort} className="!bg-transparent !text-[11px] !font-bold !text-slate-700 !uppercase !tracking-wide !px-3 !py-2 border-r border-slate-300">
                  Primary Role
                </SortableTH>
                <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-700 uppercase tracking-wide border-r border-slate-300 whitespace-nowrap">
                  Assigned Roles
                </th>
                <SortableTH columnKey="active" active={sortBy} dir={sortDir} onClick={requestSort} className="!bg-transparent !text-[11px] !font-bold !text-slate-700 !uppercase !tracking-wide !px-3 !py-2 border-r border-slate-300">
                  Status
                </SortableTH>
                <th className="px-3 py-2 text-right text-[11px] font-bold text-slate-700 uppercase tracking-wide whitespace-nowrap w-28">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton rows={6} cols={6} />
              ) : sortedUsers.length === 0 ? (
                <EmptyState colSpan={6} message="No users found" />
              ) : (
                sortedUsers.map((u, idx) => {
                  const meta = ROLE_META[u.role] || ROLE_META.employee;
                  const RoleIcon = meta.icon;
                  const isSelf = u.id === currentUser?.id;
                  const assigned = (() => {
                    const ids = [...(u.role_ids || [])];
                    if (u.role === "employee" && !ids.includes("role_employee")) ids.unshift("role_employee");
                    if (u.role === "hr_executive" && !ids.includes("role_hr_exec")) ids.unshift("role_hr_exec");
                    if (u.role === "admin" && !ids.includes("role_admin")) ids.unshift("role_admin");
                    return ids
                      .map((rid) => rolesById[rid])
                      .filter(Boolean);
                  })();
                  return (
                    <tr
                      key={u.id}
                      className={`border-b border-slate-200 ${
                        idx % 2 === 0 ? "bg-white" : "bg-[#F7F9FC]"
                      } hover:bg-[#D6E8F7]`}
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="px-3 py-2 font-medium text-slate-900 border-r border-slate-100">
                        <div className="flex items-center gap-2 min-w-[140px]">
                          <span className="truncate">{u.name || "—"}</span>
                          {isSelf && (
                            <span className="shrink-0 text-[10px] font-semibold text-[#226DB4] border border-[#226DB4]/40 px-1 rounded">
                              YOU
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700 font-mono text-[12px] border-r border-slate-100 whitespace-nowrap">
                        {u.email}
                      </td>
                      <td className="px-3 py-2 border-r border-slate-100">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-semibold rounded border ${meta.color}`}>
                          <RoleIcon className="h-3 w-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 border-r border-slate-100">
                        {assigned.length === 0 ? (
                          <span className="text-slate-400 text-[12px]">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-md" data-testid={`user-roles-${u.id}`}>
                            {assigned.map((r) => (
                              <span
                                key={r.id}
                                title={r.description || r.name}
                                className={`inline-flex items-center px-1.5 py-0.5 text-[11px] rounded border whitespace-nowrap ${
                                  r.id === "role_admin"
                                    ? "bg-rose-50 text-rose-800 border-rose-200"
                                    : r.id === "role_employee"
                                    ? "bg-slate-100 text-slate-700 border-slate-200"
                                    : r.id?.includes("payroll")
                                    ? "bg-violet-50 text-violet-800 border-violet-200"
                                    : "bg-sky-50 text-sky-800 border-sky-200"
                                }`}
                              >
                                {r.name}
                              </span>
                            ))}
                            {assigned.length > 1 && (
                              <span className="text-[10px] text-slate-500 self-center">
                                ({assigned.length} roles)
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 border-r border-slate-100">
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-semibold rounded border ${
                            u.active !== false
                              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                              : "bg-slate-100 text-slate-500 border-slate-300"
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              u.active !== false ? "bg-emerald-500" : "bg-slate-400"
                            }`}
                          />
                          {u.active !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <RowActionsMenu
                          testid={`user-actions-${u.id}`}
                          items={[
                            { label: "Edit", icon: Pencil, onClick: () => openEdit(u), testid: `edit-user-${u.id}` },
                            { label: "Reset Password", icon: KeyRound, onClick: () => openReset(u), testid: `reset-user-${u.id}` },
                            { label: u.active !== false ? "Deactivate" : "Activate", icon: u.active !== false ? UserX : UserCheck, onClick: () => toggleActive(u), disabled: isSelf, testid: `toggle-user-${u.id}` },
                            { label: "Delete", icon: Trash2, danger: true, onClick: () => setDeleting(u), disabled: isSelf, testid: `delete-user-${u.id}` },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && sortedUsers.length > 0 && (
          <div className="px-3 py-1.5 bg-[#E8EEF4] border-t border-slate-300 text-[11px] text-slate-600 flex justify-between">
            <span>Records: {sortedUsers.length}</span>
            <span>Assigned Roles column shows every RBAC role on the user (not only Primary Role)</span>
          </div>
        )}
      </div>

      {/* Unified Create / Edit user modal — scrollable body, fixed header/footer */}
      {formMode && (
        <UserFormModal
          mode={formMode}
          form={form}
          setForm={setForm}
          editing={editing}
          currentUser={currentUser}
          availableRoles={availableRoles}
          rolesById={rolesById}
          permModules={permModules}
          err={formErr}
          saving={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}

      {/* Reset password modal */}
      {resetting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={handleReset} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Reset Password</h3>
            <div className="text-sm text-slate-500">{resetting.email}</div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">New Password</label>
              <input
                required
                type="text"
                value={resetForm.new_password}
                onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })}
                placeholder="Min 8 chars, 1 upper, 1 digit, 1 special"
                className={inputCls}
                data-testid="reset-user-password"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={resetForm.must_change_password}
                onChange={(e) => setResetForm({ ...resetForm, must_change_password: e.target.checked })}
                className="rounded"
              />
              Force the user to change password on next login
            </label>
            <Banner variant="error">{resetErr}</Banner>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setResetting(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded" data-testid="reset-user-submit">
                Reset Password
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete confirmation */}
      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Delete User?</h3>
            <p className="text-sm text-slate-600">
              Permanently delete{" "}
              <span className="font-semibold text-slate-800">{deleting.name || deleting.email}</span>?
              This cannot be undone. Consider deactivating instead to preserve data.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 text-white rounded" data-testid="confirm-delete-user">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const StatCard = ({ label, value, color }) => (
  <div className="bg-white border border-slate-200 rounded shadow-sm p-4 flex items-center gap-3">
    <div className={`h-10 w-10 rounded ${color} text-white flex items-center justify-center font-bold`}>
      {value}
    </div>
    <div className="text-sm text-slate-600">{label}</div>
  </div>
);

/* ─────────── Unified User Form Modal (create + edit) ─────────── */
function UserFormModal({ mode, form, setForm, editing, currentUser, availableRoles, rolesById, permModules = [], err, saving, onClose, onSubmit }) {
  const isEdit = mode === "edit";
  const isSelf = isEdit && editing?.id === currentUser?.id;
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="user-form-modal">
      <form
        onSubmit={onSubmit}
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl flex flex-col max-h-[90vh]"
      >
        {/* Sticky header */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-200 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-slate-800">
              {isEdit ? "Edit User" : "Create New User"}
            </h3>
            {isEdit && (
              <div className="text-xs text-slate-500 mt-0.5">{editing?.email}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"
            data-testid="user-form-close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div className="px-6 py-5 overflow-y-auto grow space-y-5">
          {/* Identity */}
          <section>
            <SectionHeading>Identity</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Labeled label="Full Name *">
                <input
                  required
                  value={form.name}
                  onChange={(e) => set({ name: e.target.value })}
                  className={inputCls}
                  data-testid={isEdit ? "edit-user-name" : "create-user-name"}
                />
              </Labeled>
              <Labeled label="Email *">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => set({ email: e.target.value.toLowerCase() })}
                  className={`${inputCls} ${isEdit ? "bg-slate-100 text-slate-500" : ""}`}
                  disabled={isEdit}
                  data-testid={isEdit ? "edit-user-email" : "create-user-email"}
                />
                {isEdit && <p className="text-[11px] text-slate-500 mt-1">Email cannot be changed.</p>}
              </Labeled>
            </div>
          </section>

          {/* Access */}
          <section>
            <SectionHeading>Access</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Labeled label="Primary Role *">
                <select
                  value={form.role}
                  onChange={(e) => set({ role: e.target.value })}
                  disabled={isSelf}
                  className={inputCls}
                  data-testid={isEdit ? "edit-user-role" : "create-user-role"}
                >
                  <option value="admin">Admin (full access incl. delete & company profile)</option>
                  <option value="hr_executive">HR Executive (configurable per-module permissions)</option>
                  <option value="employee">Employee — login / self-service (not employment type)</option>
                </select>
                {isSelf && <p className="text-[11px] text-slate-500 mt-1">You cannot change your own role.</p>}
              </Labeled>
              {!isEdit && (
                <Labeled label="Temporary Password *">
                  <input
                    required
                    type="text"
                    value={form.password}
                    onChange={(e) => set({ password: e.target.value })}
                    placeholder="Min 8 chars, 1 upper, 1 digit, 1 special"
                    className={inputCls}
                    data-testid="create-user-password"
                  />
                  <label className="flex items-center gap-2 text-xs text-slate-700 mt-2">
                    <input
                      type="checkbox"
                      checked={form.must_change_password}
                      onChange={(e) => set({ must_change_password: e.target.checked })}
                      className="rounded"
                    />
                    Force password change on first login
                  </label>
                </Labeled>
              )}
            </div>
          </section>

          {/* Additional Roles + Effective Permissions */}
          {form.role !== "admin" && (
            <section>
              <SectionHeading>Assigned Roles &amp; Effective Permissions</SectionHeading>
              <div className="space-y-3 border border-slate-200 rounded-lg p-3 bg-slate-50">
                {form.role === "employee" && (
                  <p className="text-[11px] text-slate-600 bg-white border border-slate-200 rounded px-2 py-1.5">
                    Primary login role <strong>Employee (self-service)</strong> is always included.
                    Add <strong>Payroll Admin</strong> / other roles below so module menus (Payroll, etc.) appear after the user refreshes or re-logs in.
                  </p>
                )}
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                    Assigned Roles (you can select multiple)
                  </label>
                  {form.role === "employee" && (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center text-xs bg-slate-200 text-slate-700 border border-slate-300 rounded-full px-2 py-0.5">
                        Employee (login / self-service)
                      </span>
                    </div>
                  )}
                  <RoleChips
                    availableRoles={availableRoles.filter((r) => r.id !== "role_employee" && r.id !== "role_admin")}
                    selectedIds={(form.role_ids || []).filter((id) => id !== "role_employee")}
                    onChange={(ids) => {
                      const next = form.role === "employee"
                        ? ["role_employee", ...ids.filter((id) => id !== "role_employee")]
                        : ids;
                      set({ role_ids: next });
                    }}
                  />
                </div>
                <EffectivePermissionsPreview
                  roleIds={
                    form.role === "employee" && !(form.role_ids || []).includes("role_employee")
                      ? ["role_employee", ...(form.role_ids || [])]
                      : (form.role_ids || [])
                  }
                  rolesById={rolesById}
                  modules={permModules}
                />
              </div>
            </section>
          )}

          {err && <Banner variant="error">{err}</Banner>}
        </div>

        {/* Sticky footer */}
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-200 bg-white rounded hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded disabled:opacity-70"
            data-testid={isEdit ? "edit-user-submit" : "create-user-submit"}
          >
            {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save Changes" : "Create User")}
          </button>
        </div>
      </form>
    </div>
  );
}

const SectionHeading = ({ children }) => (
  <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2.5">{children}</h4>
);

const Labeled = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">{label}</label>
    {children}
  </div>
);
